import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import type { IconName } from './Icon.tsx'
import { MenuItem } from './ui.tsx'

export interface ContextAction {
  label: string
  icon: IconName
  tone?: 'fg' | 'error' | undefined
  disabled?: boolean | undefined
  onSelect(): void
}

/** Odstup menu od okraje okna. */
const EDGE_PX = 8

interface Opened {
  x: number
  y: number
  title: string | null
  actions: ContextAction[]
}

/**
 * Kontextove menu na prave tlacitko (pocitac). Vsechno v nem jde udelat
 * i jinak (detail, menu ⋯) — je to jen zkratka, na dotyku se nepouziva.
 *
 *   const { onContextMenu, contextMenu } = useContextMenu()
 *   <div onContextMenu={onContextMenu(() => [...akce], 'Nazev')}>
 */
export function useContextMenu() {
  const [opened, setOpened] = useState<Opened | null>(null)

  const onContextMenu = useCallback(
    (actions: () => ContextAction[], title: string | null = null) =>
      (event: MouseEvent<HTMLElement>) => {
        const list = actions()
        if (list.length === 0) return
        event.preventDefault()
        // Klavesa menu nebo Shift+F10 posila souradnice 0 — menu pak patri k prvku.
        const rect = event.currentTarget.getBoundingClientRect()
        const fromKeyboard = event.clientX === 0 && event.clientY === 0
        setOpened({
          x: fromKeyboard ? rect.left + 16 : event.clientX,
          y: fromKeyboard ? rect.bottom : event.clientY,
          title,
          actions: list,
        })
      },
    [],
  )

  const close = useCallback(() => setOpened(null), [])
  const contextMenu = opened && <ContextMenuPanel opened={opened} onClose={close} />
  return { onContextMenu, contextMenu, closeContextMenu: close }
}

function ContextMenuPanel({ opened, onClose }: { opened: Opened; onClose(): void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: opened.x, top: opened.y })

  // U okraje okna se menu posune dovnitr.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    setPos({
      left: Math.max(EDGE_PX, Math.min(opened.x, window.innerWidth - width - EDGE_PX)),
      top: Math.max(EDGE_PX, Math.min(opened.y, window.innerHeight - height - EDGE_PX)),
    })
    el.querySelector<HTMLElement>('button:not([disabled])')?.focus()
  }, [opened])

  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
      event.preventDefault()
      const buttons = [...(ref.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])]
      const at = buttons.indexOf(document.activeElement as HTMLElement)
      const next = buttons[(at + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length]
      next?.focus()
    }
    window.addEventListener('pointerdown', onDown, true)
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onClose, true)
    window.addEventListener('resize', onClose)
    return () => {
      window.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onClose, true)
      window.removeEventListener('resize', onClose)
    }
  }, [onClose])

  return createPortal(
    <div
      ref={ref}
      className="context-menu"
      role="menu"
      aria-label={opened.title ?? 'Možnosti'}
      style={{ left: pos.left, top: pos.top }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {opened.title && <div className="context-menu-title">{opened.title}</div>}
      {opened.actions.map((action) => (
        <MenuItem
          key={action.label}
          role="menuitem"
          icon={action.icon}
          iconTone={action.tone === 'error' ? undefined : 'muted'}
          tone={action.tone ?? 'fg'}
          label={action.label}
          disabled={action.disabled}
          onClick={() => {
            onClose()
            action.onSelect()
          }}
        />
      ))}
    </div>,
    document.body,
  )
}
